import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';

import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export enum SaveState {
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  FAILED = 'FAILED',
  UNSAVED = 'UNSAVED',
}

const TIME_TO_RESTING_STATE = 3000;

const SAVE_STATE_TO_SAVE_MESSAGE: Record<SaveState, string> = {
  [SaveState.SAVING]: 'Saving...',
  [SaveState.SAVED]: 'Saved',
  [SaveState.FAILED]: 'Saving failed',
  [SaveState.UNSAVED]: 'You have unsaved changes',
};

const SAVE_STATE_TO_ICON: Record<SaveState, () => JSX.Element> = {
  [SaveState.SAVING]: () => (
    <BenchlingDuotoneIcon
      icon="spinner-third"
      primaryColor={Colors.BLUE_LIGHT}
      secondaryColor={Colors.BLUE_POWDER_LIGHT}
      spin={true}
    />
  ),
  [SaveState.SAVED]: () => (
    <BenchlingIcon icon="check-circle" className={css(saveStatusStyles.successIcon)} />
  ),
  [SaveState.FAILED]: () => (
    <BenchlingIcon icon="status-failed" className={css(saveStatusStyles.failedIcon)} />
  ),
  [SaveState.UNSAVED]: () => <BenchlingIcon icon="warning" className={css(saveStatusStyles.warningIcon)} />,
};

export function SaveStatusIndicator(props: {
  saveStatus: SaveState;
  messageOverride?: string;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const comeToRestTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isResting, setIsResting] = useStateIfMounted(false);

  useEffect(() => {
    if (props.saveStatus === SaveState.SAVED) {
      comeToRestTimeout.current = setTimeout(() => {
        setIsResting(true);
      }, TIME_TO_RESTING_STATE);
    } else {
      setIsResting(false);
      if (comeToRestTimeout.current != null) {
        clearTimeout(comeToRestTimeout.current);
        comeToRestTimeout.current = null;
      }
    }
  }, [props.saveStatus, setIsResting]);

  return (
    <div className={css(saveStatusStyles.body)} {...getTestProps(props.dataTestAttrs)} role="status">
      <div className={css(saveStatusStyles.icon)}>
        {isResting ? null : SAVE_STATE_TO_ICON[props.saveStatus]()}
      </div>
      <div className={css(saveStatusStyles.saveStatus)}>
        {isResting ? null : (props.messageOverride ?? SAVE_STATE_TO_SAVE_MESSAGE[props.saveStatus])}
      </div>
    </div>
  );
}

/** @package */
export const saveStatusStyles = StyleSheet.create({
  body: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  saveStatus: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Colors.GRAY_DARKER,
  },
  icon: {
    marginRight: '4px',
  },
  successIcon: {
    color: Colors.GRAY_SEMIDARK,
  },
  failedIcon: {
    color: Colors.RED,
  },
  warningIcon: {
    color: Colors.ORANGE,
  },
});
